/**
 * Piezas visuales compartidas por los paneles del Resumen.
 *
 * Estan aca y no en `src/components/` porque son de esta pantalla: la cascara
 * (`components/`) la mantiene otra persona y no tiene por que crecer con cada
 * tarjeta que invente una seccion. Si manana una segunda pantalla necesita lo
 * mismo, ahi si vale promoverlo.
 *
 * La unica regla de estilo: nada de colores propios. Todo sale de los tokens de
 * `:root` en `src/styles.css`, asi que el tema claro y el oscuro siguen
 * funcionando sin una sola regla de color nueva.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Tono de una cifra o de un cartel.
 *
 * `neutral` es el default y es lo correcto para la enorme mayoria de los
 * numeros: pintar de rojo todo lo que suene mal ensenia a ignorar el rojo. Solo
 * se usa `warn` / `danger` donde hay algo que efectivamente hay que hacer.
 */
export type Tone = "neutral" | "ok" | "warn" | "danger" | "muted";

export function Badge({
  tone = "neutral",
  title,
  children,
}: {
  readonly tone?: Tone;
  readonly title?: string | undefined;
  readonly children: ReactNode;
}) {
  return (
    <span
      className={`home-badge home-badge--${tone}`}
      {...(title === undefined ? {} : { title })}
    >
      {children}
    </span>
  );
}

/**
 * Una cifra con su etiqueta y, opcionalmente, la aclaracion de que mide.
 *
 * `hint` no es decoracion: casi todos los numeros de esta pantalla necesitan
 * decir de donde salen ("filas sin ml_id", "cuenta created_at, no la fecha
 * fiscal"). Un numero sin esa linea se interpreta mal en la primera lectura.
 */
export function Figure({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly hint?: ReactNode;
  readonly tone?: Tone;
}) {
  return (
    <div className="stat-card home-figure">
      <span className={`stat-card__value home-figure__value--${tone}`}>{value}</span>
      <span className="stat-card__label">{label}</span>
      {hint === undefined ? null : <span className="home-figure__hint">{hint}</span>}
    </div>
  );
}

/**
 * Bloque con titulo, y un link opcional a la seccion que lo desarrolla.
 *
 * El link va en el encabezado y no como boton al pie porque el Resumen es
 * exactamente eso: un lugar del que se sale hacia la seccion que interesa.
 */
export function Panel({
  title,
  lead,
  to,
  toLabel,
  children,
}: {
  readonly title: string;
  readonly lead?: ReactNode;
  readonly to?: string | undefined;
  readonly toLabel?: string | undefined;
  readonly children: ReactNode;
}) {
  return (
    <section className="home-panel">
      <div className="home-panel__head">
        <h3 className="home-panel__title">{title}</h3>
        {to === undefined ? null : (
          <Link className="home-panel__link" to={to}>
            {toLabel ?? "Ver"} &rarr;
          </Link>
        )}
      </div>
      {lead === undefined ? null : <p className="home-panel__lead">{lead}</p>}
      {children}
    </section>
  );
}

/**
 * Aclaracion al pie de un panel: de donde sale el dato, o que NO dice.
 *
 * Es el componente que hace posible la regla de esta pantalla: cuando algo esta
 * vacio o es un limite superior, se explica en vez de mostrarse un cero mudo.
 */
export function Note({
  tone = "muted",
  children,
}: {
  readonly tone?: Tone;
  readonly children: ReactNode;
}) {
  return <p className={`home-note home-note--${tone}`}>{children}</p>;
}
